import { useState } from "react";
import { Routes, Route, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, BarChart3, Package, Shirt, Users, Boxes, Warehouse, BadgePercent, Settings as SettingsIcon, LogOut, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import Dashboard from "@/admin/pages/Dashboard";
import Analytics from "@/admin/pages/Analytics";
import Orders from "@/admin/pages/Orders";
import Products from "@/admin/pages/Products";
import Customers from "@/admin/pages/Customers";
import Inventory from "@/admin/pages/Inventory";
import Stock from "@/admin/pages/Stock";
import Discounts from "@/admin/pages/Discounts";
import Settings from "@/admin/pages/Settings";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/admin/orders", label: "Orders", icon: Package },
  { to: "/admin/products", label: "Products", icon: Shirt },
  { to: "/admin/discounts", label: "Discounts", icon: BadgePercent },
  { to: "/admin/customers", label: "Customers", icon: Users },
  { to: "/admin/inventory", label: "Inventory", icon: Boxes },
  { to: "/admin/stock", label: "Stock", icon: Warehouse },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const nav = (
    <nav className="flex-1 py-6 space-y-1">
      {NAV.map(({ to, label, icon: Icon, end }) => (
        <NavLink key={to} to={to} end={end} onClick={() => setOpen(false)}
          data-testid={`admin-nav-${label.toLowerCase()}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors border-l-2 ${
              isActive ? "text-chalk border-signal bg-chalk/5" : "text-graphite border-transparent hover:text-chalk"
            }`}>
          <Icon size={15} strokeWidth={1.5} /> {label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-ink text-chalk flex" data-testid="admin-dashboard-root">
      {/* desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-60 shrink-0 border-r border-border/60 min-h-screen sticky top-0 h-screen">
        <div className="px-6 h-20 flex items-center border-b border-border/60">
          <Logo variant="full" size="md" asLink={false} />
        </div>
        {nav}
        <div className="border-t border-border/60 p-4">
          <p className="font-mono text-[10px] text-graphite truncate mb-3">{user?.email}</p>
          <button onClick={doLogout} data-testid="admin-logout"
            className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-graphite hover:text-pulse transition-colors">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </aside>

      {/* mobile top bar + drawer */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-50 bg-ink/90 backdrop-blur-xl border-b border-border/60 h-16 flex items-center justify-between px-4">
        <Logo variant="full" size="md" asLink={false} />
        <button onClick={() => setOpen(!open)} data-testid="admin-mobile-menu" className="text-chalk p-2">
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 bg-ink pt-16 flex flex-col" data-testid="admin-mobile-drawer">
          {nav}
          <div className="border-t border-border/60 p-6">
            <button onClick={doLogout} data-testid="admin-logout-mobile"
              className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-graphite hover:text-pulse">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      )}

      <main className="flex-1 min-w-0 px-4 sm:px-8 py-8 pt-24 lg:pt-8">
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="orders" element={<Orders />} />
          <Route path="products" element={<Products />} />
          <Route path="discounts" element={<Discounts />} />
          <Route path="customers" element={<Customers />} />
          <Route path="inventory" element={<Inventory />} />
          <Route path="stock" element={<Stock />} />
          <Route path="settings" element={<Settings />} />
        </Routes>
      </main>
    </div>
  );
}
